import { useEffect, useState } from 'react';

import { useIsRobotConnected } from '@sbrc/hooks';
import {
  clearServicesRestarting,
  getServicesRestartingSince,
  subscribeServicesRestarting,
} from '@sbrc/services/feathers-client/host-command/servicesRestartTracker';

/**
 * A restart that never drops the botman connection was either too fast
 * to observe or never actually happened (the host command failed) —
 * don't hold the composer hostage waiting for a drop.
 */
const NO_DISCONNECT_TIMEOUT_MS = 60_000;

/**
 * Absolute ceiling, matching the host-command service timeout. A
 * restart the connection never recovers from is a robot problem the
 * composer gate can't express — release it and let sends fail loudly.
 */
const HARD_TIMEOUT_MS = 5 * 60_000;

/**
 * True while a botman-affecting `services_restart` is in flight (e.g.
 * the restart the equipment manager requires after a gripper/tooltip
 * change). The chat composer uses this to disable sends — a turn
 * started mid-restart just burns tool calls against a dead botman.
 *
 * Lifecycle: armed by `executeHostCommand` (via the restart tracker),
 * released when the botman connection is observed going down and then
 * coming back — botman is the backend the agent's tools need, so its
 * recovery is the "services are back" signal. Timeouts above cover a
 * drop that was never observed and a recovery that never comes.
 */
export function useServicesRestartGate(): boolean {
  const isConnected = useIsRobotConnected({});

  const [restartingSince, setRestartingSince] = useState(
    getServicesRestartingSince(),
  );

  const [hasDisconnected, setHasDisconnected] = useState(false);

  useEffect(
    () =>
      subscribeServicesRestarting(() => {
        setRestartingSince(getServicesRestartingSince());
      }),
    [],
  );

  useEffect(() => {
    if (restartingSince === null) {
      setHasDisconnected(false);

      return;
    }

    if (!isConnected) {
      setHasDisconnected(true);
    } else if (hasDisconnected) {
      clearServicesRestarting();
    }
  }, [restartingSince, isConnected, hasDisconnected]);

  useEffect(() => {
    if (restartingSince === null) return undefined;

    const cap = hasDisconnected ? HARD_TIMEOUT_MS : NO_DISCONNECT_TIMEOUT_MS;
    const remainingMS = Math.max(restartingSince + cap - Date.now(), 0);

    const timer = setTimeout(clearServicesRestarting, remainingMS);

    return () => clearTimeout(timer);
  }, [restartingSince, hasDisconnected]);

  return restartingSince !== null;
}
